import React, { useRef, useState } from 'react';
import { tUi } from '../utils/uiText';
import KbStatsCards from './KbStatsCards';

const statusLabelKey = {
  indexed: 'kbFileStatusIndexed',
  pending: 'kbFileStatusPending',
  noindex: 'kbFileStatusNoindex',
  skipped: 'kbFileStatusSkipped',
  error: 'kbFileStatusError',
};

export default function KnowledgePanel({
  uiLanguage = 'en',
  stats,
  syncing,
  onSync,
  onReindex,
  ingestUrl,
  onIngestUrlChange,
  onIngestUrl,
  ingesting,
  isPlatformAdmin = false,
  isOrgAdmin = false,
  canSyncKb = false,
  canFeedLlm = false,
  canManageNoindex = false,
  feedFileAccept = '',
  onUploadFiles,
  uploading = false,
  uploadFeedback = null,
  uploadVisible = false,
  onUploadVisibleChange,
  kbFiles = [],
  onToggleNoindex,
  onDeleteFile,
  noindexUpdatingPath = null,
  deletingPath = null,
  compactLayout = false,
}) {
  const fileInputRef = useRef(null);
  const [showFileList, setShowFileList] = useState(false);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length && onUploadFiles) {
      onUploadFiles(files);
    }
    e.target.value = '';
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes < 1024) return `${bytes || 0} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const canManageFiles = canManageNoindex && (onToggleNoindex || onDeleteFile);
  const fileCount = kbFiles.length;

  return (
    <div className="mb-4 bg-gray-900 rounded-lg border border-teal-500/30 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="text-lg font-semibold text-teal-400">{tUi(uiLanguage, 'kbTitle')}</h3>
          <p className="text-sm text-gray-400">
            {isPlatformAdmin
              ? tUi(uiLanguage, 'kbAdminDesc')
              : canFeedLlm
                ? (isOrgAdmin
                  ? tUi(uiLanguage, 'kbOrgAdminDesc')
                  : tUi(uiLanguage, 'kbOperatorDesc'))
                : tUi(uiLanguage, 'kbUserDesc')}
          </p>
        </div>
        {canSyncKb && (
          <div className="flex flex-wrap gap-2 shrink-0">
            <button
              type="button"
              onClick={onSync}
              disabled={syncing}
              title={tUi(uiLanguage, 'kbSyncNowHint')}
              className="px-3 py-2 text-sm bg-gray-800 hover:bg-gray-700 border border-teal-500/30 rounded-lg text-white disabled:opacity-50"
            >
              {syncing ? tUi(uiLanguage, 'kbSyncing') : tUi(uiLanguage, 'kbSyncNow')}
            </button>
            <button
              type="button"
              onClick={onReindex}
              disabled={syncing}
              title={tUi(uiLanguage, 'kbReindexHint')}
              className="px-3 py-2 text-sm bg-gray-800 hover:bg-gray-700 border border-red-500/30 rounded-lg text-red-300 disabled:opacity-50"
            >
              {tUi(uiLanguage, 'kbReindex')}
            </button>
          </div>
        )}
      </div>

      {canSyncKb && (
        <div className="mb-4 rounded-lg border border-gray-800 bg-gray-950/60 p-3 text-xs text-gray-400 space-y-2 leading-relaxed">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            {tUi(uiLanguage, 'kbSyncActionsTitle')}
          </p>
          <p>
            <span className="font-medium text-teal-300/90">{tUi(uiLanguage, 'kbSyncNow')}</span>
            {tUi(uiLanguage, 'kbSyncNowDesc')}
          </p>
          <p>
            <span className="font-medium text-red-300/90">{tUi(uiLanguage, 'kbReindex')}</span>
            {tUi(uiLanguage, 'kbReindexDesc')}
          </p>
        </div>
      )}

      {stats ? (
        <KbStatsCards
          uiLanguage={uiLanguage}
          stats={stats}
          compact={compactLayout}
          className="mb-4 text-sm"
        />
      ) : (
        <p className="text-sm text-gray-500 mb-4">{tUi(uiLanguage, 'kbStatusLoading')}</p>
      )}

      {(canFeedLlm || fileCount > 0) && (
        <div className="mb-4">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
              {tUi(uiLanguage, 'kbFilesTitle')}
              {fileCount > 0 && (
                <span className="text-gray-600 font-normal normal-case ml-1">({fileCount})</span>
              )}
            </p>
            <button
              type="button"
              onClick={() => setShowFileList((current) => !current)}
              className="px-2 py-1 text-xs rounded border border-gray-600 text-gray-300 hover:border-teal-500/40 hover:text-teal-300"
            >
              {showFileList ? tUi(uiLanguage, 'kbHideFiles') : tUi(uiLanguage, 'kbShowFiles')}
            </button>
          </div>
          {showFileList && (
            fileCount === 0 ? (
              <p className="text-sm text-gray-500">{tUi(uiLanguage, 'kbFilesEmpty')}</p>
            ) : (
              <ul className="divide-y divide-gray-800 rounded-lg border border-gray-800 overflow-hidden">
                {kbFiles.map((file) => {
                  const statusKey = statusLabelKey[file.status] || 'kbFileStatusPending';
                  const isUpdating = noindexUpdatingPath === file.path;
                  const isDeleting = deletingPath === file.path;
                  const isVisible = !file.noindex;
                  return (
                    <li
                      key={file.path}
                      className="flex flex-wrap items-center justify-between gap-3 px-3 py-2 bg-gray-800/40 text-sm"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-white truncate" title={file.path}>{file.filename}</div>
                        <div className="text-xs text-gray-500">{formatBytes(file.size)}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                        <span
                          className={`px-2 py-0.5 rounded text-xs ${
                            !isVisible
                              ? 'bg-amber-900/40 text-amber-300 border border-amber-500/30'
                              : file.status === 'indexed'
                                ? 'bg-teal-900/40 text-teal-300 border border-teal-500/30'
                                : 'bg-gray-900 text-gray-400 border border-gray-700'
                          }`}
                        >
                          {!isVisible
                            ? tUi(uiLanguage, 'kbInvisible')
                            : (file.status === 'indexed'
                              ? tUi(uiLanguage, 'kbVisible')
                              : tUi(uiLanguage, statusKey))}
                        </span>
                        {canManageFiles && onToggleNoindex && (
                          <button
                            type="button"
                            disabled={isUpdating || isDeleting || syncing || uploading}
                            onClick={() => onToggleNoindex(file.path, !file.noindex)}
                            className={`px-2.5 py-1 text-xs font-medium rounded border disabled:opacity-50 ${
                              isVisible
                                ? 'border-amber-500/40 text-amber-200 bg-amber-950/30 hover:bg-amber-950/50'
                                : 'border-teal-500/40 text-teal-200 bg-teal-950/30 hover:bg-teal-950/50'
                            }`}
                            title={
                              isVisible
                                ? tUi(uiLanguage, 'kbMakeInvisible')
                                : tUi(uiLanguage, 'kbMakeVisible')
                            }
                          >
                            {isUpdating
                              ? tUi(uiLanguage, 'kbNoindexUpdating')
                              : (isVisible
                                ? tUi(uiLanguage, 'kbMakeInvisible')
                                : tUi(uiLanguage, 'kbMakeVisible'))}
                          </button>
                        )}
                        {canManageFiles && onDeleteFile && (
                          <button
                            type="button"
                            disabled={isUpdating || isDeleting || syncing || uploading}
                            onClick={() => onDeleteFile(file.path, file.filename)}
                            className="px-2.5 py-1 text-xs font-medium rounded border border-red-500/40 text-red-300 bg-red-950/30 hover:bg-red-950/50 disabled:opacity-50"
                          >
                            {isDeleting ? tUi(uiLanguage, 'kbDeleting') : tUi(uiLanguage, 'kbDeleteFile')}
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )
          )}
        </div>
      )}

      {canFeedLlm && (
        <div className="mb-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
            {tUi(uiLanguage, 'kbUploadTitle')}
          </p>

          <label className="flex items-start gap-2 mb-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={uploadVisible}
              onChange={(e) => onUploadVisibleChange?.(e.target.checked)}
              disabled={uploading}
              className="mt-0.5 rounded border-gray-600 bg-gray-900 text-teal-500 focus:ring-teal-500/40"
            />
            <span>
              <span className="text-sm text-white">{tUi(uiLanguage, 'kbUploadVisibleLabel')}</span>
              <span className="block text-xs text-gray-500">{tUi(uiLanguage, 'kbUploadVisibleHint')}</span>
            </span>
          </label>

          <div
            onClick={() => !uploading && fileInputRef.current?.click()}
            onKeyDown={(e) => e.key === 'Enter' && !uploading && fileInputRef.current?.click()}
            role="button"
            tabIndex={0}
            className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg p-5 cursor-pointer transition-colors
              ${uploading
                ? 'border-teal-500/20 opacity-60 cursor-not-allowed'
                : 'border-teal-500/40 hover:border-teal-400 hover:bg-gray-800/50'
              }`}
          >
            {uploading ? (
              <>
                <svg className="animate-spin h-6 w-6 text-teal-400" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span className="text-sm text-teal-300">{tUi(uiLanguage, 'kbUploading')}</span>
              </>
            ) : (
              <>
                <svg className="h-8 w-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                <span className="text-sm text-white font-medium">{tUi(uiLanguage, 'kbUploadButton')}</span>
                <span className="text-xs text-gray-500">
                  {isPlatformAdmin
                    ? tUi(uiLanguage, 'kbUploadHint')
                    : isOrgAdmin
                      ? tUi(uiLanguage, 'kbUploadHintAdmin')
                      : tUi(uiLanguage, 'kbUploadHintOperator')}
                </span>
              </>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            accept={feedFileAccept}
            onChange={handleFileChange}
          />

          {uploadFeedback && (
            <div
              className={`mt-2 px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${
                uploadFeedback.type === 'success'
                  ? 'bg-teal-900/40 border border-teal-500/40 text-teal-300'
                  : 'bg-red-900/40 border border-red-500/40 text-red-300'
              }`}
            >
              {uploadFeedback.type === 'success' ? (
                <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              <span>{uploadFeedback.message}</span>
            </div>
          )}
        </div>
      )}

      {isPlatformAdmin && (
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
            {tUi(uiLanguage, 'kbIngestSectionTitle')}
          </p>
          <div className="flex flex-col md:flex-row gap-2">
            <input
              value={ingestUrl}
              onChange={(e) => onIngestUrlChange(e.target.value)}
              placeholder={tUi(uiLanguage, 'kbIngestPlaceholder')}
              className="flex-1 px-3 py-2 bg-black border border-teal-500/30 rounded-lg text-white text-sm"
            />
            <button
              type="button"
              onClick={onIngestUrl}
              disabled={ingesting || !ingestUrl.trim()}
              className="px-4 py-2 bg-teal-500 hover:bg-teal-400 disabled:bg-gray-700 text-black font-semibold rounded-lg text-sm whitespace-nowrap"
            >
              {ingesting ? tUi(uiLanguage, 'kbIngesting') : tUi(uiLanguage, 'kbIngest')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
